/**
 * Daybreak Bundle - Shopify JavaScript
 * Copy this file to your theme's assets/ folder
 * Include in theme.liquid before </body>: <script src="{{ 'daybreak.js' | asset_url }}" defer></script>
 */

(function() {
'use strict';

// ============================================
// AUDIO PLAYER
// ============================================
class AudioPlayer {
  constructor() {
    this.currentlyPlaying = null;
    this.audioElements = {};
    this.progressIntervals = {};
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const playBtn = e.target.closest('[data-audio-play]');
      if (playBtn) {
        this.handlePlayClick(playBtn);
      }

      const waveform = e.target.closest('[data-audio-waveform]');
      if (waveform) {
        this.handleWaveformClick(e, waveform);
      }

      // Scrubber bar click handler (for master preview)
      const scrubber = e.target.closest('[data-audio-scrubber]');
      if (scrubber) {
        this.handleScrubberClick(e, scrubber);
      }
    });
  }

  handlePlayClick(btn) {
    const audioId = btn.dataset.audioPlay;
    const audioUrl = btn.dataset.audioUrl;

    if (!audioUrl) return;

    if (this.currentlyPlaying === audioId) {
      this.pause(audioId);
    } else {
      if (this.currentlyPlaying) {
        this.pause(this.currentlyPlaying);
      }
      this.play(audioId, audioUrl);
    }
  }

  play(audioId, audioUrl) {
    let audio = this.audioElements[audioId];

    if (!audio) {
      audio = new Audio(audioUrl);
      this.audioElements[audioId] = audio;

      audio.addEventListener('loadedmetadata', () => {
        this.updateDuration(audioId, audio.duration);
      });

      audio.addEventListener('ended', () => {
        this.onEnded(audioId);
      });
    }

    audio.play();
    this.currentlyPlaying = audioId;
    this.setPlayingState(audioId, true);
    this.startProgressUpdate(audioId, audio);
  }

  pause(audioId) {
    const audio = this.audioElements[audioId];
    if (audio) {
      audio.pause();
    }
    this.currentlyPlaying = null;
    this.setPlayingState(audioId, false);
    this.stopProgressUpdate(audioId);
  }

  onEnded(audioId) {
    this.currentlyPlaying = null;
    this.setPlayingState(audioId, false);
    this.stopProgressUpdate(audioId);
    this.updateProgress(audioId, 0);
  }

  setPlayingState(audioId, isPlaying) {
    const playerContainer = document.querySelector(`[data-audio-player="${audioId}"]`);
    const playBtn = document.querySelector(`[data-audio-play="${audioId}"]`);
    if (playerContainer) {
      playerContainer.classList.toggle('is-playing', isPlaying);
    }
    if (playBtn) {
      playBtn.classList.toggle('is-playing', isPlaying);
      const playIcon = playBtn.querySelector('.play-icon');
      const pauseIcon = playBtn.querySelector('.pause-icon');
      if (playIcon) playIcon.style.display = isPlaying ? 'none' : 'block';
      if (pauseIcon) pauseIcon.style.display = isPlaying ? 'block' : 'none';
    }
  }

  startProgressUpdate(audioId, audio) {
    this.stopProgressUpdate(audioId);
    this.progressIntervals[audioId] = setInterval(() => {
      if (audio.duration) {
        const progress = audio.currentTime / audio.duration;
        this.updateProgress(audioId, progress);
      }
    }, 50);
  }

  stopProgressUpdate(audioId) {
    if (this.progressIntervals[audioId]) {
      clearInterval(this.progressIntervals[audioId]);
      delete this.progressIntervals[audioId];
    }
  }

  updateProgress(audioId, progress) {
    // Waveform bars (for pack preview players)
    const waveform = document.querySelector(`[data-audio-waveform="${audioId}"]`);
    if (waveform) {
      const bars = waveform.querySelectorAll('.db-waveform-bar');
      bars.forEach((bar, i) => {
        const barProgress = i / bars.length;
        const isActive = barProgress < progress;
        bar.classList.toggle('is-active', isActive);

        // Daybreak style: warm white inactive, bright white active
        bar.style.setProperty(
          'background-color',
          isActive ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.2)',
          'important'
        );
      });
    }

    // Scrubber bar (for master preview player)
    const scrubber = document.querySelector(`[data-audio-scrubber="${audioId}"]`);
    if (scrubber) {
      const fill = scrubber.querySelector('.db-scrubber-fill');
      if (fill) {
        // Use setProperty with !important to override init styles
        fill.style.setProperty('width', `${progress * 100}%`, 'important');
      }
    }

    // Update duration display with current time for scrubber-style players
    const audio = this.audioElements[audioId];
    if (audio && audio.duration) {
      const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
      if (durationEl && durationEl.textContent.includes('/')) {
        // Format as "current / total" for scrubber-style players
        durationEl.textContent = `${this.formatTime(audio.currentTime)} / ${this.formatTime(audio.duration)}`;
      }
    }
  }

  updateDuration(audioId, duration) {
    const durationEl = document.querySelector(`[data-audio-duration="${audioId}"]`);
    if (durationEl) {
      // Check if it's a scrubber-style player (has "/" in default text)
      if (durationEl.textContent.includes('/')) {
        durationEl.textContent = `0:00 / ${this.formatTime(duration)}`;
      } else {
        durationEl.textContent = this.formatTime(duration);
      }
    }
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  handleWaveformClick(e, waveform) {
    const audioId = waveform.dataset.audioWaveform;
    const audio = this.audioElements[audioId];
    if (!audio || !audio.duration) return;

    const rect = waveform.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    audio.currentTime = progress * audio.duration;
    this.updateProgress(audioId, progress);
  }

  handleScrubberClick(e, scrubber) {
    const audioId = scrubber.dataset.audioScrubber;
    const audio = this.audioElements[audioId];
    if (!audio || !audio.duration) return;

    const rect = scrubber.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progress = Math.max(0, Math.min(1, clickX / rect.width));
    audio.currentTime = progress * audio.duration;
    this.updateProgress(audioId, progress);
  }
}

// ============================================
// STICKY BAR
// ============================================

class StickyBar {
  constructor() {
    this.stickyBar = document.querySelector('.db-sticky-bar');
    this.triggerElement = document.getElementById('audio-preview');
    this.triggerOffset = 100;
    if (this.stickyBar) {
      this.init();
    }
  }

  init() {
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });
    this.handleScroll();
  }

  handleScroll() {
    if (!this.triggerElement) {
      // Fallback: show after scrolling 500px
      const shouldShow = window.scrollY > 500;
      this.stickyBar.classList.toggle('is-visible', shouldShow);
      return;
    }

    const rect = this.triggerElement.getBoundingClientRect();
    const shouldShow = rect.top <= this.triggerOffset;
    this.stickyBar.classList.toggle('is-visible', shouldShow);
  }
}

// ============================================
// FAQ ACCORDION
// ============================================

class FAQAccordion {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('click', (e) => {
      const question = e.target.closest('.db-faq-question');
      if (question) {
        this.toggle(question);
      }
    });
  }

  toggle(questionBtn) {
    const faqItem = questionBtn.closest('.db-faq-item');
    if (!faqItem) return;

    const isOpen = faqItem.classList.contains('is-open');
    // Close all other items (optional - remove for multi-open)
    document.querySelectorAll('.db-faq-item.is-open').forEach(item => {
      if (item !== faqItem) {
        item.classList.remove('is-open');
      }
    });

    faqItem.classList.toggle('is-open', !isOpen);
  }
}

// ============================================
// LAZY LOADING WITH INTERSECTION OBSERVER
// ============================================

class LazyLoader {
  constructor() {
    this.init();
  }

  init() {
    const lazyElements = document.querySelectorAll('[data-lazy]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.load(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '100px'
      });

      lazyElements.forEach(el => observer.observe(el));
    } else {
      // Fallback for older browsers
      lazyElements.forEach(el => this.load(el));
    }
  }

  load(element) {
    // Load lazy images
    const lazySrc = element.dataset.lazySrc;
    if (lazySrc && element.tagName === 'IMG') {
      element.src = lazySrc;
    }

    // Remove skeleton/placeholder classes
    element.classList.remove('is-loading');
    element.classList.add('is-loaded');
    element.removeAttribute('data-lazy');
  }
}

// ============================================
// MARQUEE ANIMATION CONTROL
// ============================================

class MarqueeController {
  constructor() {
    this.marquees = document.querySelectorAll('.db-marquee');
    this.init();
  }

  init() {
    // Pause on hover (optional)
    this.marquees.forEach(marquee => {
      marquee.addEventListener('mouseenter', () => {
        marquee.style.animationPlayState = 'paused';
      });
      marquee.addEventListener('mouseleave', () => {
        marquee.style.animationPlayState = 'running';
      });
    });

    // Reduce motion for users who prefer it
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.marquees.forEach(marquee => {
        marquee.style.animation = 'none';
      });
    }
  }
}

// ============================================
// SCROLL ANIMATIONS
// ============================================

class ScrollAnimations {
  constructor() {
    this.init();
  }

  init() {
    const animatedElements = document.querySelectorAll('[data-animate-on-scroll]');
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('db-animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1
      });

      animatedElements.forEach(el => observer.observe(el));
    }
  }
}

// ============================================
// WAVEFORM GENERATOR (Optional Enhancement)
// ============================================

function generateWaveformBars(container, data) {
  if (!container) return;

  // Ensure container is visible with flex layout - reduced gap for more bars
  container.style.setProperty('display', 'flex', 'important');
  container.style.setProperty('align-items', 'center', 'important');
  container.style.setProperty('justify-content', 'space-between', 'important');
  container.style.setProperty('gap', '1px', 'important');
  container.style.setProperty('height', '32px', 'important');
  container.style.setProperty('min-height', '32px', 'important');
  container.style.setProperty('box-sizing', 'border-box', 'important');
  container.style.setProperty('padding', '0 2px', 'important');
  container.style.setProperty('visibility', 'visible', 'important');
  container.style.setProperty('opacity', '1', 'important');

  // If no waveform data, just make existing Liquid bars visible
  if (!data) {
    container.querySelectorAll('.db-waveform-bar').forEach((bar) => {
      bar.style.setProperty('opacity', '1', 'important');
      bar.style.setProperty('visibility', 'visible', 'important');
      bar.style.setProperty('display', 'block', 'important');
      bar.style.setProperty('background-color', 'rgba(255, 255, 255, 0.2)', 'important');
    });
    return;
  }

  // Interpolate data to double the bar count
  const interpolatedData = [];
  for (let i = 0; i < data.length; i++) {
    interpolatedData.push(data[i]);
    // Add interpolated value between current and next
    if (i < data.length - 1) {
      const avg = (data[i] + data[i + 1]) / 2;
      // Add slight variation for natural look
      interpolatedData.push(avg * (0.9 + Math.random() * 0.2));
    }
  }
  // Add one more at the end
  interpolatedData.push(data[data.length - 1] * 0.85);

  container.innerHTML = '';
  interpolatedData.forEach((height) => {
    const bar = document.createElement('div');
    bar.className = 'db-waveform-bar';
    // Thinner bars (max 3px), more rounded, match Daybreak style
    bar.style.cssText = `
      height: ${Math.max(height * 100, 12)}% !important;
      min-height: 4px !important;
      flex: 1 1 0 !important;
      min-width: 1px !important;
      max-width: 3px !important;
      border-radius: 1.5px !important;
      opacity: 1 !important;
      visibility: visible !important;
      display: block !important;
      background-color: rgba(255, 255, 255, 0.2) !important;
      transition: background-color 0.1s ease !important;
    `;

    container.appendChild(bar);
  });
}

// ============================================
// SCRUBBER SAFETY STYLES (prevents theme CSS from hiding it)
// ============================================

function initializeScrubbers() {
  // Also ensure the parent player container is visible
  document.querySelectorAll('.db-player--master').forEach((player) => {
    player.style.setProperty('display', 'flex', 'important');
    player.style.setProperty('visibility', 'visible', 'important');
    player.style.setProperty('opacity', '1', 'important');
  });

  document.querySelectorAll('[data-audio-scrubber]').forEach((scrubber) => {
    // Force visibility with !important to override any theme CSS
    scrubber.style.setProperty('display', 'block', 'important');
    scrubber.style.setProperty('position', 'relative', 'important');
    scrubber.style.setProperty('height', '6px', 'important');
    scrubber.style.setProperty('min-height', '6px', 'important');
    scrubber.style.setProperty('width', '100%', 'important');
    scrubber.style.setProperty('border-radius', '9999px', 'important');
    scrubber.style.setProperty('overflow', 'hidden', 'important');
    scrubber.style.setProperty('background-color', 'rgba(255, 255, 255, 0.1)', 'important');
    scrubber.style.setProperty('cursor', 'pointer', 'important');
    scrubber.style.setProperty('visibility', 'visible', 'important');
    scrubber.style.setProperty('opacity', '1', 'important');

    let fill = scrubber.querySelector('.db-scrubber-fill');
    if (!fill) {
      fill = document.createElement('div');
      fill.className = 'db-scrubber-fill';
      scrubber.appendChild(fill);
    }

    // Force fill visibility
    fill.style.setProperty('display', 'block', 'important');
    fill.style.setProperty('position', 'absolute', 'important');
    fill.style.setProperty('left', '0', 'important');
    fill.style.setProperty('top', '0', 'important');
    fill.style.setProperty('bottom', '0', 'important');
    fill.style.setProperty('height', '100%', 'important');
    // Don't set width here with !important - let updateProgress control it
    fill.style.setProperty('width', '0%');
    fill.style.setProperty('border-radius', '9999px', 'important');
    fill.style.setProperty('background-color', 'var(--db-accent-gold)', 'important');
    fill.style.setProperty('visibility', 'visible', 'important');
    fill.style.setProperty('opacity', '1', 'important');
  });

  console.info('[Daybreak] Scrubbers initialized:', document.querySelectorAll('[data-audio-scrubber]').length);
}

// Pre-defined waveform data (from original React implementation)
const waveformData = {
"scp-preview-5-twilight-76-2": [0.74,0.57,0.94,0.62,0.97,0.84,0.7,0.81,0.94,0.81,0.37,0.96,0.81,0.84,0.77,0.89,0.82,0.95,0.62,0.45,1,0.85,0.84,0.57,0.94,0.62,0.96,0.81,0.76,0.81,0.95,0.66,0.23,0.37,0.12],
  "scp-preview-4-elysium-83": [1,0.72,0.34,0.44,0.92,0.56,0.66,0.43,0.67,0.4,0.62,0.32,0.57,0.94,0.67,0.33,0.42,0.89,0.68,0.65,0.44,0.77,0.72,0.65,0.34,0.45,0.65,0.57,0.46,0.37,0.59,0.39,0.26,0.09,0.04],
  "scp-preview-3-hades-74": [0.96,0.6,0.68,0.39,1,0.63,0.4,0.8,0.86,0.77,0.5,0.66,0.72,0.97,0.39,0.33,0.31,0.76,0.81,0.63,0.79,0.9,0.8,0.54,0.69,0.36,0.98,0.53,0.67,0.76,0.83,0.52,0.26,0.2,0.03],
  "scp-preview-2-heavenly-75": [1,0.69,0.84,0.62,0.93,0.9,0.51,0.93,0.84,0.93,0.48,0.85,0.69,1,0.66,0.79,0.92,0.65,0.86,0.44,0.82,0.76,0.8,0.54,0.75,0.75,0.88,0.57,0.43,0.8,0.76,0.6,0.24,0.26,0.1],
  "portrait-4wanna-stop-94": [0.78,0.46,0.5,0.71,0.47,0.82,0.77,0.44,0.72,0.59,0.57,0.49,0.72,0.64,0.76,0.9,0.45,0.73,0.53,0.45,0.67,0.56,0.69,0.72,0.71,0.55,1,0.71,0.47,0.73,0.42,0.57,0.5,0.31,0.05],
  "portrait-4two-stories-89-50": [0.68,0.52,0.7,0.52,0.77,0.53,0.81,0.74,1,0.84,0.7,0.64,0.49,0.68,0.6,0.78,0.86,0.79,0.66,0.67,0.83,0.8,0.75,0.57,0.78,0.87,0.79,0.61,0.6,0.31,0.52,0.51,0.63,0.62,0.15],
  "portrait-4stay-this-time-86": [0.62,0.34,0.78,0.88,0.81,0.35,0.69,0.65,1,0.5,0.45,0.5,0.94,0.7,0.33,0.73,0.65,0.75,0.35,0.5,0.52,0.27,0.21,0.58,0.46,0.3,0.43,0.45,0.38,0.54,0.48,0.54,0.45,0.23,0.08],
  "portrait-4drizzly-93": [0.35,0.58,0.91,0.71,0.81,0.4,0.88,1,0.58,0.31,0.78,0.75,0.72,0.94,0.55,0.98,0.79,0.79,0.33,0.99,0.6,0.74,0.66,0.7,0.97,0.78,0.44,0.42,0.99,0.65,0.79,0.53,0.37,0.37,0.13],
  "portrait-3nightmare-83": [0.55,0.41,0.37,0.46,0.43,0.49,0.45,0.52,0.61,0.58,0.45,0.45,0.46,0.76,1,0.53,0.48,0.52,0.43,0.39,0.42,0.44,0.46,0.47,0.5,0.53,0.64,0.5,0.44,0.44,0.6,0.75,0.47,0.18,0.06],
  "portrait-3hurt-me-85": [0.97,0.62,0.73,0.7,0.67,0.62,0.65,0.76,0.65,0.63,0.6,0.77,0.61,0.68,0.64,0.69,0.73,0.79,0.89,0.63,0.77,0.6,0.67,0.66,0.93,0.63,0.74,0.62,0.63,0.8,0.92,1,0.42,0.31,0.1],
  "portrait-3human-86": [1,0.58,0.45,0.42,0.85,0.72,0.58,0.54,0.93,0.86,0.52,0.45,0.42,0.92,0.66,0.55,0.72,0.53,0.99,0.53,0.26,0.39,0.88,0.72,0.27,0.33,0.7,0.89,0.4,0.29,0.7,0.66,0.25,0.2,0.2],
  "portrait-3found-my-way-83": [0.58,0.55,0.55,0.61,0.63,0.46,0.6,0.74,0.97,0.52,0.56,0.52,0.61,0.62,0.58,0.52,0.67,0.73,0.47,0.58,0.58,0.61,0.55,0.56,0.54,1,0.72,0.51,0.56,0.69,0.7,0.52,0.37,0.17,0.07],
  "portrait-2sinner-78": [0.68,0.78,0.66,0.85,0.75,0.52,0.89,0.81,0.93,0.64,0.8,0.68,0.88,1,0.93,0.89,0.86,0.86,0.63,0.87,0.66,0.88,0.58,0.6,0.85,0.84,0.83,0.72,0.76,0.9,0.87,0.85,0.49,0.27,0.13],
  "portrait-2pacifism-76": [0.39,0.4,0.47,0.52,0.62,0.58,0.66,0.64,0.55,0.36,0.41,0.47,0.6,0.56,0.61,0.66,0.62,0.63,0.68,0.69,0.67,0.87,0.68,0.71,0.63,0.54,1,0.75,0.68,0.78,0.7,0.44,0.36,0.18,0.07],
  "portrait-2over-and-over-and-over-75": [0.42,0.79,0.6,0.85,0.82,0.91,0.71,0.98,0.71,0.57,0.71,0.46,0.86,0.95,0.81,0.76,1,0.61,0.73,0.63,0.8,0.82,0.78,0.31,0.91,0.88,0.63,0.79,0.64,0.57,0.87,0.45,0.29,0.2,0.1],
  "portrait-2carry-me-home-80": [0.88,0.95,0.9,0.64,0.47,0.55,0.62,0.52,0.6,0.88,0.95,0.86,0.59,0.45,0.56,0.44,0.46,0.75,0.87,1,0.77,0.54,0.43,0.56,0.66,0.46,0.41,0.31,0.27,0.3,0.45,0.5,0.28,0.11,0.06],
  "portrait-1take-a-bow-70": [0.81,0.82,0.92,0.87,0.87,0.93,0.99,0.94,0.92,0.8,0.89,0.92,0.85,0.88,1,0.91,0.96,0.78,0.76,0.85,0.86,0.79,0.85,0.77,0.83,0.78,0.72,0.81,0.84,0.85,0.78,0.66,0.46,0.29,0.1],
  "portrait-1seaside-74": [0.4,0.62,0.68,0.66,0.65,0.64,0.69,0.91,0.98,0.53,0.67,0.76,0.73,0.49,0.62,0.66,0.77,0.86,0.56,0.66,0.7,0.63,0.69,0.65,0.67,1,0.83,0.66,0.72,0.75,0.63,0.48,0.6,0.39,0.18],
  "portrait-1morale-74": [0.6,0.55,0.59,0.49,0.58,0.52,0.5,0.48,0.63,0.99,0.96,1,0.97,0.95,0.99,0.75,0.67,0.74,0.58,0.7,0.6,0.55,0.65,0.49,0.56,0.71,0.73,0.92,0.72,0.7,0.69,0.67,0.62,0.35,0.15],
  "portrait-1golf-club-72": [1,0.75,0.36,0.28,0.2,0.16,0.14,0.13,0.14,0.17,0.15,0.14,0.12,0.85,0.93,0.87,0.65,0.78,0.95,0.53,0.3,0.25,0.18,0.15,0.14,0.12,0.17,0.16,0.14,0.13,0.54,0.88,0.68,0.37,0.06],
};

// Initialize all waveforms with pre-defined data
function initializeWaveforms() {
  const containers = Array.from(document.querySelectorAll('[data-audio-waveform]'));
  const missing = [];

  containers.forEach((container) => {
    const audioId = container.dataset.audioWaveform;
    const data = waveformData[audioId];

    // Always call generateWaveformBars - it handles both cases:
    // - With data: generates new bars from waveformData
    // - Without data: makes existing Liquid placeholder bars visible
    generateWaveformBars(container, data);

    if (!data) {
      missing.push(audioId);
    }
  });

  // Helpful debug (shows up in Shopify theme preview console)
  if (missing.length) {
    console.warn('[Daybreak] Missing waveformData keys for:', missing);
  }
  console.info('[Daybreak] Waveform containers found:', containers.length);
}

// ============================================
// INITIALIZE ON DOM READY
// ============================================

function init() {
  new AudioPlayer();
  new StickyBar();
  new FAQAccordion();
  new LazyLoader();
  new MarqueeController();
  new ScrollAnimations();
  initializeScrubbers();
  initializeWaveforms();
}

// Guard against multiple initializations
if (window.DaybreakInitialized) return;
window.DaybreakInitialized = true;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for external use if needed
window.Daybreak = {
  AudioPlayer,
  StickyBar,
  FAQAccordion,
  waveformData,
  generateWaveformBars
};

})();
